import Alert from '@customink/pigment-react/lib/components/Alert';
import type { Product } from '@customink/project-service-react-components';
import { useAtomValue } from 'jotai';

import {
  HIGH_COLOR_DESIGN_MIN_ORDER_QUANTITY,
  WCIBMO_NAMES,
} from '@/constants';
import { useOrderQuantityType } from '@/hooks/useOrderQuantityType';
import { hasValidZipCodeAtom } from '@/hooks/useValidateZipCode';
import { pageViewTag } from '@/utils/metrics';

type AddItemMinimumColorQtyProps = {
  product?: Product;
  selectedColorId?: number;
  isHighColorDesign?: boolean;
};

export default function AddItemMinimumQuantity({
  product,
  selectedColorId,
  isHighColorDesign = false,
}: AddItemMinimumColorQtyProps) {
  const color = product?.colors.find((color) => color.id === selectedColorId);
  const hasValidZipCode = useAtomValue(hasValidZipCodeAtom);
  const quantityType = useOrderQuantityType();

  const minQuantity =
    isHighColorDesign && !color?.dtg_enabled
      ? HIGH_COLOR_DESIGN_MIN_ORDER_QUANTITY
      : product?.minimum_bulk_quantity;

  if (!product || !color) return null;
  if (color.singles_enabled) return null;

  if (hasValidZipCode) {
    pageViewTag(
      `checkout/summary/${quantityType}/wcibmo/${WCIBMO_NAMES.RAISE_ABOVE_MIN_OR_PRODUCT}`,
    );
  }

  return (
    <Alert data-testid="min-qty-alert" severity="error">
      {product.has_singles_enabled_colors ? (
        <>
          <strong>
            A minimum order quantity of {minQuantity} is needed to use the color{' '}
            {color.name}.
          </strong>{' '}
          Please either:
          <ul>
            <li>Increase the order quantity to {minQuantity} or more, or</li>{' '}
            <li>Pick a color that is available for any size order.</li>
          </ul>
        </>
      ) : (
        <strong>
          A minimum order quantity of {minQuantity} is needed for this order.
          Please pick a color that is available for any size order.
        </strong>
      )}
    </Alert>
  );
}
